import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useCMS } from '../../context/CMSContext';
import { Save, Calculator, DollarSign, Percent, RefreshCw } from 'lucide-react';

const AdminROISettingsPage = () => {
  const { getROISettings, updateROISettings } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    default_participants: 50,
    default_staff: 20,
    default_admin_hours: 40,
    default_hourly_rate: 35,
    time_savings_percent: 60,
    compliance_savings_percent: 40,
    claiming_improvement_percent: 15,
    setup_cost: 18500,
    monthly_hosting: 390,
    cta_title: '',
    cta_subtitle: '',
    cta_button_text: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getROISettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load ROI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateROISettings(settings);
      toast.success('ROI settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">ROI Calculator Settings</h1>
          <p className="text-gray-500">Configure the ROI calculator page content and default values</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-tech-blue hover:bg-tech-blue/90">
          {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          Save Changes
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-tech-blue" />
            Hero Section
          </CardTitle>
          <CardDescription>Configure the main heading and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Hero Title</label>
            <Input
              value={settings.hero_title}
              onChange={(e) => handleChange('hero_title', e.target.value)}
              placeholder="Calculate Your ROI"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
            <Input
              value={settings.hero_subtitle}
              onChange={(e) => handleChange('hero_subtitle', e.target.value)}
              placeholder="See how much time and money..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Default Calculator Values */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-care-teal" />
            Default Calculator Values
          </CardTitle>
          <CardDescription>Set default values for the ROI calculator inputs</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Default Participants</label>
            <Input
              type="number"
              value={settings.default_participants}
              onChange={(e) => handleChange('default_participants', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Default Staff</label>
            <Input
              type="number"
              value={settings.default_staff}
              onChange={(e) => handleChange('default_staff', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Default Admin Hours/Week</label>
            <Input
              type="number"
              value={settings.default_admin_hours}
              onChange={(e) => handleChange('default_admin_hours', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Default Hourly Rate ($)</label>
            <Input
              type="number"
              value={settings.default_hourly_rate}
              onChange={(e) => handleChange('default_hourly_rate', parseInt(e.target.value) || 0)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Savings Multipliers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-green-500" />
            Savings Multipliers
          </CardTitle>
          <CardDescription>Configure the percentage savings shown in calculations</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Time Savings (%)</label>
            <Input
              type="number"
              value={settings.time_savings_percent}
              onChange={(e) => handleChange('time_savings_percent', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Compliance Savings (%)</label>
            <Input
              type="number"
              value={settings.compliance_savings_percent}
              onChange={(e) => handleChange('compliance_savings_percent', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Claiming Improvement (%)</label>
            <Input
              type="number"
              value={settings.claiming_improvement_percent}
              onChange={(e) => handleChange('claiming_improvement_percent', parseInt(e.target.value) || 0)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-500" />
            Pricing Display
          </CardTitle>
          <CardDescription>Configure pricing shown in the calculator</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Setup Cost ($)</label>
            <Input
              type="number"
              value={settings.setup_cost}
              onChange={(e) => handleChange('setup_cost', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Monthly Hosting ($)</label>
            <Input
              type="number"
              value={settings.monthly_hosting}
              onChange={(e) => handleChange('monthly_hosting', parseInt(e.target.value) || 0)}
            />
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call to Action Section</CardTitle>
          <CardDescription>Configure the bottom CTA section content</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">CTA Title</label>
            <Input
              value={settings.cta_title}
              onChange={(e) => handleChange('cta_title', e.target.value)}
              placeholder="Ready to See These Savings?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Subtitle</label>
            <Input
              value={settings.cta_subtitle}
              onChange={(e) => handleChange('cta_subtitle', e.target.value)}
              placeholder="Book a personalised demo..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Button Text</label>
            <Input
              value={settings.cta_button_text}
              onChange={(e) => handleChange('cta_button_text', e.target.value)}
              placeholder="Book Your Free Demo"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminROISettingsPage;
